import {
  ChatApiService,
  SendMessageParams,
} from '../chatApiService';
import {
  ApiFileInfo,
  HistoryResponse,
  CaptionApiResponse,
  ChatSession,
} from '@/types/chat';

/**
 * Options for configuring mock service behavior
 */
export interface MockChatApiServiceOptions {
  /**
   * Delay between stream events in milliseconds (default: 50ms)
   */
  delayBetweenEvents?: number;

  /**
   * Whether to simulate errors
   */
  shouldError?: boolean;

  /**
   * Error message to return if shouldError is true
   */
  errorMessage?: string;

  /**
   * Whether to simulate rate limiting
   */
  shouldRateLimit?: boolean;

  /**
   * Mock conversations to return
   */
  mockConversations?: any[];

  /**
   * Mock history to return
   */
  mockHistory?: any[];
}

/**
 * Creates a mock implementation of ChatApiService for testing
 */
export function createMockChatApiService(
  options: MockChatApiServiceOptions = {}
): ChatApiService {
  const {
    delayBetweenEvents = 50,
    shouldError = false,
    errorMessage = 'Mock error',
    shouldRateLimit = false,
    mockConversations = [],
    mockHistory = [],
  } = options;

  return {
    async sendMessage(params: SendMessageParams): Promise<ReadableStream> {
      if (shouldRateLimit) {
        // Simulate the monthly usage rate limit error that matches production API response        // The error should match the structure returned by /api/agent/chat-messages when rate limited
        const rateLimitError: any = new Error('Rate limit exceeded');
        rateLimitError.status = 429;
        
        // Mock monthly usage values (matching free tier limit from plan_monthly_limits)
        const mockLimit = 50; // Free tier monthly limit
        const mockUsed = 50; // User has used all messages
        const mockRemaining = 0; // No messages remaining
        
        rateLimitError.limit = mockLimit;
        rateLimitError.used = mockUsed;
        rateLimitError.remaining = mockRemaining;
        
        // The error message should match what the API returns
        // This simulates the JSON response body from the API route
        rateLimitError.message = JSON.stringify({
          error: 'Your monthly message limit has been exceeded. Please upgrade your tier to continue.',
          limit: mockLimit,
          used: mockUsed,
          remaining: mockRemaining,
        });
        
        throw rateLimitError;
      }

      if (shouldError) {
        throw new Error(errorMessage);
      }

      // Dynamically import mock data to avoid bundling in production
      // This properly simulates the full workflow behavior
      const { createMockStreamResponse } = await import('../../../tests/fixtures/chat/mock-data');
      const mockReader = await createMockStreamResponse(params.message, {
        delayBetweenEvents,
      });

      // Convert the reader to a ReadableStream
      return new ReadableStream({
        async start(controller) {
          try {
            while (true) {
              const { done, value } = await mockReader.read();
              if (done) {
                controller.close();
                break;
              }
              controller.enqueue(value);
            }
          } catch (error) {
            controller.error(error);
          }
        },
      });
    },

    async stopStreaming(taskId: string, userId: string): Promise<void> {
      console.log(`[Mock] Stopping streaming for task ID: ${taskId}`);
      // Mock implementation - just resolves
      return Promise.resolve();
    },

    async fetchConversations(userId: string, limit: number = 20): Promise<any> {
      console.log(`[Mock] Fetching conversations for user: ${userId}, limit: ${limit}`);
      return {
        data: mockConversations.slice(0, limit),
      };
    },

    async fetchHistory(conversationId: string, userId?: string): Promise<HistoryResponse> {
      console.log(`[Mock] Fetching history for conversation: ${conversationId}`);
      return {
        data: mockHistory,
      };
    },

    async renameConversation(
      conversationId: string,
      newTitle: string,
      userId: string
    ): Promise<any> {
      console.log(`[Mock] Renaming conversation ${conversationId} to "${newTitle}"`);
      return {
        id: conversationId,
        name: newTitle,
      };
    },

    async deleteConversation(conversationId: string, userId: string): Promise<boolean> {
      console.log(`[Mock] Deleting conversation ${conversationId}`);
      return true;
    },

    async uploadFile(file: File, userId: string): Promise<ApiFileInfo> {
      console.log(`[Mock] Uploading file: ${file.name}`);
      return {
        id: `mock-file-${Date.now()}`,
        name: file.name,
        type: file.type,
        size: file.size,
        url: URL.createObjectURL(file),
        extension: file.name.split('.').pop() || '',
        created_at: Date.now(),
      };
    },

    async captionFile(file: File, userId: string): Promise<CaptionApiResponse> {
      console.log(`[Mock] Getting caption for file: ${file.name}`);
      return {
        caption: `Mock caption for ${file.name}`,
      };
    },

    async uploadUrlAsFile(url: string, userId: string): Promise<ApiFileInfo[]> {
      console.log(`[Mock] Uploading URL as file: ${url}`);
      return [
        {
          id: `mock-url-${Date.now()}`,
          name: new URL(url).pathname.split('/').pop() || url,
          type: 'custom',
          size: 0,
          url: url,
          extension: url.split('.').pop()?.split('?')[0] || '',
          created_at: Date.now(),
        },
      ];
    },

    async captionUrl(url: string, userId: string): Promise<CaptionApiResponse> {
      console.log(`[Mock] Getting caption for URL: ${url}`);
      return {
        caption: `Mock caption for ${url}`,
      };
    },

    async postFeedback(
      difyMessageId: string,
      rating: 'like' | 'dislike',
      userId: string
    ): Promise<any> {
      console.log(`[Mock] Posting feedback ${rating} for message ${difyMessageId}`);
      return {
        message_id: difyMessageId,
        rating,
      };
    },

    async createRemoteConversation(userId?: string): Promise<ChatSession | null> {
      console.log(`[Mock] Creating remote conversation for user: ${userId}`);
      return null; // Mock assumes implicit creation
    },
  };
}

